import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import html2canvas from 'html2canvas';
import Spinner from '../components/Spinner';
import SuccessNotification from '../components/notifications/SuccessNotification';
import CashCalculatorPopup, { BillCount } from '../components/CashCalculatorPopup';
import { Shift, User } from '../interfaces';
import '../components/CloseShift.css';
import { Property } from '../Toolbar';
import ZReportView, { ZReportViewRef } from '../components/ZReportView';

interface ZReportData {
    id: string;
    propertyId: string;
    shiftStartDate: string;
    shiftEndDate: string;
    shiftId: string;
    createdByUserWithId: string;
    createdByUserWithName: string;
    grossSales: number;
    discounts: number;
    mealPlanDeductions: number;
    returns: number;
    netSales: number;
    taxes: number;
    serviceCharge: number;
    totalRevenue: number;
    expectedCash: number;
    actualCashCount: number;
    detectedVariance: number;
    creditCards: number;
    roomCharges: number;
    numberOfReceipts: number;
    numberOfVoidOrders: number;
    firstOrderNumber: string;
    lastOrderNumber: string;
    varianceNotes: string;
    isUserReport: boolean;
    nextShiftFloat?: number;
    bankDeposit?: number;
    bankBagNumber?: string;
}

const initialBills: BillCount[] = [
    { denomination: 100, count: 0, label: '$100 BILLS' },
    { denomination: 50, count: 0, label: '$50 BILLS' },
    { denomination: 20, count: 0, label: '$20 BILLS' },
    { denomination: 10, count: 0, label: '$10 BILLS' },
    { denomination: 5, count: 0, label: '$5 BILLS' },
    { denomination: 1, count: 0, label: '$1 BILLS' },
];

type ShiftStep = 'count' | 'review' | 'deposit' | 'complete';

const CloseUserShift: React.FC = () => {
    const navigate = useNavigate();
    const zReportRef = useRef<ZReportViewRef>(null);
    const [showSpinner, setShowSpinner] = useState(false);
    const [currentShift, setCurrentShift] = useState<Shift | null>(null);
    const [zReportData, setZReportData] = useState<ZReportData | null>(null);
    const [actualCashCount, setActualCashCount] = useState<number>(0);
    const [varianceNotes, setVarianceNotes] = useState('');
    const [showSuccess, setShowSuccess] = useState(false);
    const [headerTitle, setHeaderTitle] = useState('');
    const [content, setContent] = useState('');
    const [isCashCalculatorOpen, setIsCashCalculatorOpen] = useState(false);
    const [currentStep, setCurrentStep] = useState<ShiftStep>('count');
    const [bills, setBills] = useState<BillCount[]>(initialBills);
    const [looseCoins, setLooseCoins] = useState<number>(0);
    const [currentProperty, setCurrentProperty] = useState<Property | null>(null);
    const [user, setUser] = useState<User | null>(null);
    // Floating Cash & Bank Deposit step (between Review and Clock Out).
    const [nextShiftFloat, setNextShiftFloat] = useState<number>(0);
    const [bankDeposit, setBankDeposit] = useState<number>(0);
    const [bankBagNumber, setBankBagNumber] = useState('');
    const [clockOutUser, setClockOutUser] = useState(true);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setShowSpinner(true);
        try {
            // Fetch property
            const storedProperty = localStorage.getItem('currentProperty');
            if (storedProperty) {
                setCurrentProperty(JSON.parse(storedProperty));
            }

            // Fetch current user
            const userResponse = await fetch(`${process.env.REACT_APP_API_URL}/User/get-by-email`, {
                credentials: 'include',
            });
            if (userResponse.ok) {
                const userData = await userResponse.json();
                setUser(userData);
            }

            // Fetch current shift
            const shiftResponse = await fetch(`${process.env.REACT_APP_API_URL}/Shift/current`, {
                credentials: 'include',
            });
            if (shiftResponse.ok) {
                const shiftData = await shiftResponse.json();
                setCurrentShift(shiftData);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setShowSpinner(false);
        }
    };

    const handleReviewShift = () => {
        setCurrentStep('review');
    };

    const handleCloseUserShift = async () => {
        try {
            setShowSpinner(true);
            const response = await fetch(`${process.env.REACT_APP_API_URL}/Shift/close-user-shift`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    actualCashCount,
                    varianceNotes,
                    nextShiftFloat,
                    bankDeposit,
                    bankBagNumber,
                    clockOutUser,
                }),
            });

            if (response.ok) {
                const data: ZReportData = await response.json();
                setZReportData(data);
                setCurrentStep('complete');
            } else {
                const errorText = await response.text();
                console.error('Failed to close user shift:', errorText);
                setHeaderTitle("Error");
                setContent("Failed to close your shift. Please try again.");
                setShowSuccess(true);
                setTimeout(() => setShowSuccess(false), 3000);
            }
        } catch (error) {
            console.error('Error closing user shift:', error);
            setHeaderTitle("Error");
            setContent("An error occurred while closing your shift.");
            setShowSuccess(true);
            setTimeout(() => setShowSuccess(false), 3000);
        } finally {
            setShowSpinner(false);
        }
    };

    const handleCancel = () => {
        if (currentStep === 'review') {
            setCurrentStep('count');
        } else if (currentStep === 'deposit') {
            setCurrentStep('review');
        } else {
            navigate('/profile');
        }
    };

    const handleApplyCashCount = (total: number) => {
        setActualCashCount(total);
    };

    const handleSaveZReport = async () => {
        try {
            setShowSpinner(true);
            const receiptElement = zReportRef.current?.getReceiptElement();
            if (!receiptElement) {
                throw new Error('Receipt element not found');
            }

            const canvas = await html2canvas(receiptElement, {
                scale: 2,
                useCORS: true,
                backgroundColor: '#ffffff',
            });

            const link = document.createElement('a');
            link.download = `user-z-report-${zReportData?.shiftId || 'unknown'}.png`;
            link.href = canvas.toDataURL('image/png');
            link.click();

            setHeaderTitle("Success!");
            setContent("Z Report saved as PNG.");
            setShowSuccess(true);
            setTimeout(() => setShowSuccess(false), 2000);
        } catch (error) {
            console.error('Error saving Z Report:', error);
        } finally {
            setShowSpinner(false);
        }
    };

    const handlePrintZReport = () => {
        window.print();
    };

    const handleDone = () => {
        navigate('/profile');
    };

    const formatCurrency = (value: number) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(value);
    };

    const convertToShiftFormat = (data: ZReportData): Shift => ({
        id: data.id,
        propertyId: data.propertyId,
        startDate: data.shiftStartDate,
        endDate: data.shiftEndDate,
        shiftId: data.shiftId,
        startedByUserWithId: data.createdByUserWithId,
        startedByUserWithName: data.createdByUserWithName,
        closedByUserWithId: data.createdByUserWithId,
        closedByUserWithName: data.createdByUserWithName,
        actualCashCount: data.actualCashCount,
        creditCards: data.creditCards,
        roomCharges: data.roomCharges,
        grossSales: data.grossSales,
        discounts: data.discounts,
        mealPlanDeductions: data.mealPlanDeductions,
        returns: data.returns,
        netSales: data.netSales,
        taxes: data.taxes,
        serviceCharge: data.serviceCharge ?? 0,
        totalRevenue: data.totalRevenue,
        firstOrderNumber: data.firstOrderNumber,
        lastOrderNumber: data.lastOrderNumber,
        numberOfVoidOrders: data.numberOfVoidOrders,
        numberOfReceipts: data.numberOfReceipts,
        expectedCash: data.expectedCash,
        detectedVariance: data.detectedVariance,
        varianceNotes: data.varianceNotes,
        nextShiftFloat: 0,
        bankDeposit: 0,
        bankBagNumber: '',
    });

    // Show Z Report View after shift is closed
    if (currentStep === 'complete' && zReportData) {
        return (
            <>
                <ZReportView
                    ref={zReportRef}
                    shift={convertToShiftFormat(zReportData)}
                    property={currentProperty}
                    onSave={handleSaveZReport}
                    onEmail={() => { }}
                    onPrint={handlePrintZReport}
                    onNewShift={handleDone}
                    isUserReport={true}
                />
                {showSpinner && <Spinner />}
                {showSuccess && <SuccessNotification headerTitle={headerTitle} content={content} />}
            </>
        );
    }

    // Floating Cash & Bank Deposit screen (between Review and Clock Out)
    if (currentStep === 'deposit') {
        return (
            <div className="close-shift-page ps-scope">
                <div className="close-shift-container">
                    <div className="close-shift-main">
                        <div className="close-shift-header">
                            <div>
                                <h3>Floating Cash &amp; Bank Deposit</h3>
                                <p className="subtitle">Set the next shift's float and record the bank deposit before clocking out.</p>
                            </div>
                        </div>

                        <div className="review-section">
                            <div className="deposit-fields">
                                <div className="deposit-field-card">
                                    <label>Next Shift Float</label>
                                    <div className="deposit-input-row">
                                        <span className="deposit-currency">$</span>
                                        <input
                                            type="number"
                                            inputMode="decimal"
                                            value={nextShiftFloat}
                                            onChange={(e) => setNextShiftFloat(parseFloat(e.target.value) || 0)}
                                        />
                                    </div>
                                    <p className="deposit-hint">Cash left in the drawer to start the next shift.</p>
                                </div>

                                <div className="deposit-field-card">
                                    <label>Bank Deposit</label>
                                    <div className="deposit-input-row">
                                        <span className="deposit-currency">$</span>
                                        <input
                                            type="number"
                                            inputMode="decimal"
                                            value={bankDeposit}
                                            onChange={(e) => setBankDeposit(parseFloat(e.target.value) || 0)}
                                        />
                                    </div>
                                    <p className="deposit-hint">Cash sent to the bank.</p>
                                </div>
                            </div>

                            <div className="deposit-field-card">
                                <label>Bank Bag #</label>
                                <input
                                    type="text"
                                    className="deposit-text-input"
                                    value={bankBagNumber}
                                    onChange={(e) => setBankBagNumber(e.target.value)}
                                    placeholder="e.g. BB-00123"
                                />
                                <label className="deposit-checkbox">
                                    <input
                                        type="checkbox"
                                        checked={clockOutUser}
                                        onChange={(e) => setClockOutUser(e.target.checked)}
                                    />
                                    <span>Clock out user</span>
                                </label>
                            </div>
                        </div>
                    </div>

                    <div className="close-shift-sidebar">
                        <div className="shift-info">
                            <h3 className="shift-number">User Z-Report</h3>
                            <p className="company-name">{user?.firstName} {user?.lastName}</p>
                        </div>

                        <div className="current-sales">
                            <span>Your Sales:</span>
                            <span className="sales-value">{formatCurrency(currentShift?.totalRevenue ?? 0)}</span>
                        </div>

                        <div className="sidebar-actions">
                            <button
                                className="review-shift-btn"
                                onClick={handleCloseUserShift}
                            >
                                Clock Out &amp; Generate Report
                                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </button>

                            <div className="secondary-actions">
                                <button className="secondary-btn cancel" onClick={handleCancel}>
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                    Back
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
                {showSpinner && <Spinner />}
                {showSuccess && <SuccessNotification headerTitle={headerTitle} content={content} />}
            </div>
        );
    }

    // Review screen
    if (currentStep === 'review') {
        const expectedCash = currentShift?.expectedCash ?? 0;
        const variance = actualCashCount - expectedCash;

        return (
            <div className="close-shift-page ps-scope">
                <div className="close-shift-container">
                    <div className="close-shift-main">
                        <div className="close-shift-header">
                            <div>
                                <h3>Review Your Shift</h3>
                                <p className="subtitle">Verify your cash count before clocking out.</p>
                            </div>
                        </div>

                        <div className="review-section">
                            <div className="review-card">
                                <h4>Cash Summary</h4>
                                <div className="review-row">
                                    <span>Expected Cash</span>
                                    <span>{formatCurrency(expectedCash)}</span>
                                </div>
                                <div className="review-row">
                                    <span>Actual Cash Count</span>
                                    <span>{formatCurrency(actualCashCount)}</span>
                                </div>
                                <div className={`review-row variance ${variance === 0 ? 'balanced' : variance > 0 ? 'over' : 'short'}`}>
                                    <span>Variance</span>
                                    <span>
                                        {variance === 0 ? '✓ Balanced' : `${variance > 0 ? '+' : ''}${formatCurrency(variance)}`}
                                    </span>
                                </div>

                                <div className="summary-stats">
                                    <div className="summary-stat">
                                        <div className="summary-stat-label">Credit Cards</div>
                                        <div className="summary-stat-value blue">
                                            {formatCurrency(currentShift?.creditCards ?? 0)}
                                        </div>
                                    </div>
                                    <div className="summary-stat">
                                        <div className="summary-stat-label">Room Charges</div>
                                        <div className="summary-stat-value purple">
                                            {formatCurrency(currentShift?.roomCharges ?? 0)}
                                        </div>
                                    </div>
                                    <div className="summary-stat">
                                        <div className="summary-stat-label">Total Revenue</div>
                                        <div className="summary-stat-value green">
                                            {formatCurrency(currentShift?.totalRevenue ?? 0)}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {variance !== 0 && (
                                <div className="variance-notes-section">
                                    <label>
                                        {variance > 0 ? '💰 Cash Over - ' : '⚠️ Cash Short - '}
                                        Variance Notes (Optional)
                                    </label>
                                    <textarea
                                        value={varianceNotes}
                                        onChange={(e) => setVarianceNotes(e.target.value)}
                                        placeholder="Explain the variance (e.g., tip left on table, counting error, etc.)..."
                                        rows={4}
                                    />
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="close-shift-sidebar">
                        <div className="shift-info">
                            <h3 className="shift-number">User Z-Report</h3>
                            <p className="company-name">{user?.firstName} {user?.lastName}</p>
                        </div>

                        <div className="current-sales">
                            <span>Your Sales:</span>
                            <span className="sales-value">{formatCurrency(currentShift?.totalRevenue ?? 0)}</span>
                        </div>

                        <div className="sidebar-actions">
                            <button
                                className="review-shift-btn"
                                onClick={() => setCurrentStep('deposit')}
                            >
                                Continue to Deposit
                                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </button>

                            <div className="secondary-actions">
                                <button className="secondary-btn cancel" onClick={handleCancel}>
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                    Back
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
                {showSpinner && <Spinner />}
                {showSuccess && <SuccessNotification headerTitle={headerTitle} content={content} />}
            </div>
        );
    }

    // Count screen (initial)
    return (
        <div className="close-shift-page ps-scope">
            <div className="close-shift-container">
                <div className="close-shift-main">
                    <div className="close-shift-header">
                        <div>
                            <h3>Clock Out: Cash Count</h3>
                            <p className="subtitle">Count your drawer before clocking out.</p>
                        </div>
                    </div>

                    <div className="close-shift-cards">
                        <div className="shift-card cash-count-card">
                            <div className="card-header">
                                <span className="card-title">Your Cash Count</span>
                                <span className="card-icon">$</span>
                            </div>
                            <div className="cash-input-container">
                                <span className="currency-symbol">$</span>
                                <input
                                    type="number"
                                    className="cash-input"
                                    value={actualCashCount}
                                    onChange={(e) => setActualCashCount(parseFloat(e.target.value) || 0)}
                                    placeholder="0"
                                />
                            </div>
                            <div className="cash-tabs">
                                <button
                                    className="cash-tab active"
                                    onClick={() => setIsCashCalculatorOpen(true)}
                                >
                                    COUNT
                                </button>
                            </div>
                        </div>

                        <div className="shift-card" style={{ backgroundColor: '#f0f6ff' }}>
                            <div className="card-header">
                                <span className="card-title" style={{ color: '#354d96' }}>Your Credit Cards</span>
                            </div>
                            <div className="card-value" style={{ color: '#354d96' }}>
                                {formatCurrency(currentShift?.creditCards ?? 0)}
                            </div>
                        </div>

                        <div className="shift-card" style={{ backgroundColor: '#faf6ff' }}>
                            <div className="card-header">
                                <span className="card-title" style={{ color: '#936cb2' }}>Your Room Charges</span>
                            </div>
                            <div className="card-value" style={{ color: '#936cb2' }}>
                                {formatCurrency(currentShift?.roomCharges ?? 0)}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="close-shift-sidebar">
                    <div className="shift-info">
                        <h3 className="shift-number">User Clock Out</h3>
                        <p className="company-name">{user?.firstName} {user?.lastName}</p>
                    </div>

                    <div className="current-sales">
                        <span>Your Sales:</span>
                        <span className="sales-value">{formatCurrency(currentShift?.totalRevenue ?? 0)}</span>
                    </div>

                    <div className="sidebar-actions">
                        <button
                            className="review-shift-btn"
                            onClick={handleReviewShift}
                        >
                            Review & Clock Out
                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </button>

                        <div className="secondary-actions">
                            <button className="secondary-btn cancel" onClick={handleCancel}>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {showSpinner && <Spinner />}
            {showSuccess && <SuccessNotification headerTitle={headerTitle} content={content} />}

            <CashCalculatorPopup
                isOpen={isCashCalculatorOpen}
                onClose={() => setIsCashCalculatorOpen(false)}
                onApply={handleApplyCashCount}
                bills={bills}
                setBills={setBills}
                looseCoins={looseCoins}
                setLooseCoins={setLooseCoins}
            />
        </div>
    );
};

export default CloseUserShift;